// Centralized design tokens — the single source of truth for brand colors.
//
// These values are lifted verbatim from the legacy .NET theme (Site/CSS/tiny.css
// on everywhere-stable.thinkingcap.com). LoginPage.tsx already hard-coded the
// page/button gradients and link yellow; those exact strings now live here so the
// whole app can reference one place instead of scattering hex literals.
//
// Anything that should respond to the light/dark toggle must come from the MUI
// theme (palette.*, palette.gradients.*, palette.accent.main) — NOT from a raw
// hex in an sx prop. These tokens feed makeTheme(); components should prefer the
// theme keys. The few exceptions (genuinely fixed brand colors) live under
// BRAND_EXTERNAL.

// ─── Gradients (legacy) ──────────────────────────────────────────────────────
export const PAGE_GRADIENT_DARK = 'linear-gradient(90deg, #2f0762 0%, #15023d 100%)';
export const CARD_GRADIENT = 'linear-gradient(154.52deg, #503d9f 0%, #3c288a 100%)';
export const BUTTON_GRADIENT = 'linear-gradient(0deg, #f77c1e -133.27%, #f3bc16 105.93%)';
export const BUTTON_GRADIENT_HOVER = 'linear-gradient(0deg, #f3bc16 -133.27%, #f77c1e 105.93%)';

// ─── Brand colors ────────────────────────────────────────────────────────────
export const GOLD = '#f5a623';          // accent / secondary (icons, highlights)
export const GOLD_DARK = '#d4880f';
export const GOLD_LIGHT = '#ffb830';
export const LINK_YELLOW = '#fcd520';   // content links on dark surfaces

// Purple scale (deep → light)
export const PURPLE_BG_START = '#2f0762';
export const PURPLE_BG_END = '#15023d';
export const PURPLE_DEEP = '#16053d';   // AppBar / darkest chrome
export const PURPLE_DARKEST = '#0d0e20';
export const PURPLE_CARD_START = '#503d9f';
export const PURPLE_CARD_END = '#3c288a';
export const PURPLE_MAIN = '#451f92';
export const PURPLE_PRIMARY = '#3A2080';
export const PURPLE_PRIMARY_DARK = '#2D1B6B';
export const PURPLE_PRIMARY_LIGHT = '#4B2D9E';
export const PURPLE_ACCENT = '#8347b3';
export const PURPLE_MUTED = '#765da1';
export const PURPLE_BORDER = '#4A30A0';
export const PURPLE_AVATAR = '#321D80';
export const PURPLE_FOOTER = '#1A0F4F';

// Stacked-card decoration (legacy `.badgeCard_LP` layered look — two rotated
// layers peeking out behind a badge card so it reads as a stack). NEAR is the
// front decorative layer, FAR sits furthest back. Light-mode variants are soft
// purple tints so the stack still reads on the off-white page.
export const STACK_NEAR = '#6469ea';        // periwinkle (front layer) — legacy .lp-bg::before
export const STACK_FAR = '#8347b3';         // purple    (back layer)  — legacy .lp-bg::after
export const STACK_NEAR_LIGHT = '#cfc9ef';
export const STACK_FAR_LIGHT = '#bdaee6';

// Status
export const SUCCESS_GREEN = '#28c67d';
export const SUCCESS_GREEN_DARK = '#2e8b57';
export const ERROR_RED = '#f44336';
export const WARNING_ORANGE = '#ff9800';

// Neutral
export const WHITE = '#FFFFFF';
export const TEXT_DARK = '#1A1A2E';
export const TEXT_MUTED_DARK = '#555066';
export const LIGHT_BG = '#F6F5F8';
export const DIVIDER_LIGHT = '#E0DCEA';
export const DIVIDER_DARK = 'rgba(255,255,255,0.16)';

// Fixed brand colors that never change with the theme (external services,
// editor/canvas chrome). Kept out of the palette on purpose.
export const BRAND_EXTERNAL = {
    facebook: '#1877f2',
    linkedin: '#0a66c2',
    google: '#4285f4',
} as const;
